import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Auth
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LoginPage } from "./components/auth/LoginPage";
import { AuthCallback } from "./components/auth/AuthCallback";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ErrorBoundary } from "./components/common/ErrorBoundary";

// Dashboard
import { DashboardLayout } from "./components/dashboard/DashboardLayout";
import { DashboardOverview } from "./pages/DashboardOverview";
import { ChatbotsPage } from "./pages/ChatbotsPage";
import { ChatbotDetailPage } from "./pages/ChatbotDetailPage";
import { SettingsPage } from "./pages/SettingsPage";
import { BrandingPage } from "./pages/BrandingPage";
import { AdminPage } from "./pages/AdminPage";
import { SSOPage } from "./pages/SSOPage";
import { UserManualPage } from "./pages/UserManualPage";
import { LegalPage } from "./pages/LegalPage";
import { ResellerBenefitsPage } from "./pages/ResellerBenefitsPage";
import { PricingPage } from "./pages/PricingPage";
import ContactPage from "./pages/ContactPage";
import ReferAndEarnPage from "./pages/ReferAndEarnPage";
import ResellerDashboard from "./pages/ResellerDashboard";
import ResellerLeaderboard from "./pages/ResellerLeaderboard";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import RefAndEarn from "./pages/RefAndEarn";
import LeadsPipelinePage from "./pages/LeadsPipelinePage";
import AuditLogsPage from "./pages/AuditLogsPage";
import EmailChannelPage from "./pages/EmailChannelPage";
import AutomationsPage from "./pages/AutomationsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ModeratorPerformancePage from "./pages/ModeratorPerformancePage";

// Public Pages
import UseCasesPage from "./pages/UseCasesPage";
import FAQPage from "./pages/FAQPage";
import CaseStudiesPage from "./pages/CaseStudiesPage";

// SEO/Industry Pages
import IndustriesPage from "./pages/IndustriesPage";
import IndustryPage from "./pages/IndustryPage";
import SmallBusinessPage from "./pages/SmallBusinessPage";
import EnterprisePage from "./pages/EnterprisePage";
import LocationPage from "./pages/LocationPage";
import LocationsPage from "./pages/LocationsPage";
import HomePage from "./pages/HomePage";

// Common Components
import { GDPRConsentBanner } from "./components/common/GDPRConsentBanner";

// Email Verification Page
import { VerifyEmailPage } from "./pages/VerifyEmailPage";

// Router component to handle auth callback detection
const AppRouter = () => {
  const location = useLocation();

  // Referral persistence: capture any ?aff=/?ref=/?referral= param the visitor
  // arrives with and stash it in localStorage. LoginPage reads it back when the
  // user reaches /signup so referrals survive multi-page navigation flows
  // (marketing home → pricing → signup). Fixes iter124 bug #14.
  React.useEffect(() => {
    try {
      const q = new URLSearchParams(location.search);
      const code = q.get('aff') || q.get('ref') || q.get('referral');
      if (code) localStorage.setItem('harmonix_ref_code', code);
    } catch { /* ignore */ }
  }, [location.search]);

  // Route auth-callback fragments to <AuthCallback /> so we never render the
  // Protected Dashboard with raw session_id / error params still in the URL.
  // We check for BOTH ``session_id=`` (success path) and ``error=`` (Google
  // consent cancelled / access_denied) — the latter previously fell through
  // to the API and rendered a raw JSON error page. See AuthCallback for the
  // graceful "Sign-in was cancelled" UX.
  const hashHasAuthPayload = location.hash?.includes('session_id=') || location.hash?.includes('error=');
  const searchHasAuthError = location.search?.includes('error=') && (
    location.search.includes('access_denied') || location.search.includes('consent')
  );
  if (hashHasAuthPayload || searchHasAuthError) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<LoginPage />} />
      <Route path="/register" element={<Navigate to={`/signup${location.search || ''}`} replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Legal Pages (SEO-friendly URLs) */}
      <Route path="/legal/:page" element={<LegalPage />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/cookies" element={<Navigate to="/legal/cookies" replace />} />
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
      <Route path="/acceptable-use" element={<Navigate to="/legal/acceptable-use" replace />} />
      <Route path="/billing-faq" element={<Navigate to="/legal/billing-faq" replace />} />
      
      {/* Pricing shows the full PricingPage with Custom Plan Builder */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/book-demo" element={<Navigate to="/contact?type=demo" replace />} />
      <Route path="/refer-and-earn" element={<ReferAndEarnPage />} />
      <Route path="/refer" element={<Navigate to="/refer-and-earn" replace />} />
      
      {/* Protected Dashboard Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardOverview />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/chatbots" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ChatbotsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/chatbots/:chatbotId" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ChatbotDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/accounts/:accountId/branding" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <BrandingPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/accounts/:accountId/sso" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SSOPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/admin" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdminPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/refer" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <RefAndEarn />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/reseller" 
        element={<Navigate to="/dashboard/refer?tab=reseller" replace />}
      />
      <Route 
        path="/dashboard/leaderboard" 
        element={<Navigate to="/dashboard/refer?tab=leaderboard" replace />}
      />
      <Route 
        path="/dashboard/affiliate" 
        element={<Navigate to="/dashboard/refer?tab=earnings" replace />}
      />
      <Route 
        path="/dashboard/leads" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <LeadsPipelinePage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/leads/:chatbotId" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <LeadsPipelinePage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/audit-logs" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AuditLogsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/email-channel" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EmailChannelPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/automations" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AutomationsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/analytics" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AnalyticsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/dashboard/moderator-performance"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ModeratorPerformancePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Public User Manual Page */}
      <Route path="/docs/user-manual" element={<UserManualPage />} />
      
      {/* Public Reseller Benefits Page */}
      <Route path="/reseller" element={<ResellerBenefitsPage />} />
      <Route path="/partners" element={<ResellerBenefitsPage />} />
      
      {/* Public Use Cases Page */}
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/solutions" element={<UseCasesPage />} />
      
      {/* Public FAQ Page */}
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/help" element={<FAQPage />} />
      
      {/* Case Studies & Blog */}
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/case-studies/:slug" element={<CaseStudiesPage />} />
      
      {/* SEO/Industry Pages */}
      <Route path="/industries" element={<IndustriesPage />} />
      <Route path="/ai-for-plumbers" element={<IndustryPage />} />
      <Route path="/ai-for-lawyers" element={<IndustryPage />} />
      <Route path="/ai-for-clinics" element={<IndustryPage />} />
      <Route path="/ai-for-real-estate" element={<IndustryPage />} />
      <Route path="/ai-for-consultants" element={<IndustryPage />} />
      <Route path="/ai-for-agencies" element={<IndustryPage />} />
      <Route path="/ai-for-dental" element={<IndustryPage />} />
      <Route path="/ai-for-hvac" element={<IndustryPage />} />
      <Route path="/ai-for-auto" element={<IndustryPage />} />
      <Route path="/ai-for-salons" element={<IndustryPage />} />
      <Route path="/ai-for-fitness" element={<IndustryPage />} />
      <Route path="/small-business" element={<SmallBusinessPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      
      {/* Location-Based SEO Pages */}
      <Route path="/locations" element={<LocationsPage />} />
      <Route path="/ai-receptionist/:locationId" element={<LocationPage />} />
      
      <Route path="/blog" element={<CaseStudiesPage />} />
      
      {/* Default redirect */}
      <Route path="/" element={<HomePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <ErrorBoundary>
                <AppRouter />
              </ErrorBoundary>
              <Toaster 
                position="top-right" 
                toastOptions={{
                  style: {
                    background: '#1e293b',
                    border: '1px solid #334155',
                    color: '#f1f5f9',
                  },
                }}
              />
              <GDPRConsentBanner />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
